// SkipUpdateJobModal — search an active Skip job then act on it, without
// leaving Collections. Mirrors the legacy operations "Update Active Job"
// popup.
//
// Two phases driven by local state:
//   Phase 1 (search) — filter the active-jobs list (all non-terminal jobs,
//     via useActiveActionJobs — the same source the Skip Active Jobs page
//     uses) by job number / customer / postcode, render slim clickable rows.
//   Phase 2 (act)    — show the chosen job's summary + DynamicActionButtons,
//     which fetches the job's available actions and renders the existing
//     action modals (collection / exchange / tip-return / confirm /
//     mark-delivered). On action completion we return to Phase 1.
//
// Job enc_ids rotate per response, so we never cache them across fetches —
// the selected job object is taken straight from the current list render
// and handed to DynamicActionButtons for the immediate action call.

import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { useToast } from '../../../hooks/useToast';
import { useActiveActionJobs } from '../../skip/hooks/useSkipJobs';
import { SkipJobStatusBadge } from '../../skip/jobs/components/SkipJobStatusBadge';
import { DynamicActionButtons } from '../../skip/jobs/components/DynamicActionButtons';
import type { SkipJobSummary } from '../../../types/api';
import styles from './SkipUpdateJobModal.module.css';

export interface SkipUpdateJobModalProps {
  open: boolean;
  onClose: () => void;
}

function addressLine(job: SkipJobSummary): string {
  const line1 = job.site_address_line1?.trim();
  const postcode = job.site_postcode?.trim() || job.customer_postcode?.trim();
  return [line1, postcode].filter(Boolean).join(', ') || '—';
}

export function SkipUpdateJobModal({
  open,
  onClose,
}: SkipUpdateJobModalProps): JSX.Element {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<SkipJobSummary | null>(null);

  const query = useActiveActionJobs({ page: 1, limit: 100 });
  const jobs = query.data?.jobs ?? [];

  // Reset to a clean search every time the modal opens.
  useEffect(() => {
    if (open) {
      setQ('');
      setSelected(null);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return jobs;
    return jobs.filter(
      (j) =>
        j.job_number.toLowerCase().includes(term) ||
        j.customer_name.toLowerCase().includes(term) ||
        j.customer_postcode.toLowerCase().includes(term),
    );
  }, [q, jobs]);

  function handleActionComplete(): void {
    toast.success('Job updated');
    setSelected(null);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Update Active Job"
      size="lg"
    >
      {selected === null ? (
        <div className={styles.searchPhase}>
          <input
            type="search"
            className={styles.search}
            placeholder="Search by job number, customer or postcode…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search active jobs"
          />

          {query.isLoading ? (
            <p className={styles.empty}>Loading…</p>
          ) : filtered.length === 0 ? (
            <p className={styles.empty}>
              {q.trim()
                ? 'No active jobs match your search.'
                : 'No active jobs at this time.'}
            </p>
          ) : (
            <ul className={styles.list}>
              {filtered.map((job) => (
                <li key={job.enc_id}>
                  <button
                    type="button"
                    className={styles.jobRow}
                    onClick={() => setSelected(job)}
                  >
                    <span className={styles.jobMain}>
                      <span className={styles.jobNumber}>{job.job_number}</span>
                      <span className={styles.customer}>{job.customer_name}</span>
                    </span>
                    <span className={styles.jobMeta}>
                      <span className={styles.address}>{addressLine(job)}</span>
                      <SkipJobStatusBadge status={job.status} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className={styles.actPhase}>
          <button
            type="button"
            className={styles.back}
            onClick={() => setSelected(null)}
          >
            ← Back to search
          </button>

          <div className={styles.summary}>
            <div className={styles.summaryHead}>
              <span className={styles.jobNumber}>{selected.job_number}</span>
              <SkipJobStatusBadge status={selected.status} />
            </div>
            <p className={styles.summaryCustomer}>{selected.customer_name}</p>
            <p className={styles.summaryAddress}>{addressLine(selected)}</p>
          </div>

          <div className={styles.actions}>
            <DynamicActionButtons
              jobEncId={selected.enc_id}
              jobNumber={selected.job_number}
              customerName={selected.customer_name}
              skipSizeEncId={selected.skip_size_enc_id}
              onActionComplete={handleActionComplete}
            />
          </div>
        </div>
      )}

      <div className={styles.footer}>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
}
